import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, TrendingUp, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { API } from '@/utils/api';
import { useFeatures } from '@/context/FeaturesContext';

const MOOD_COLORS = {
  uncloned: '#df1283',
  custom_branded: '#df1283',
  magical: '#df1283',
  amazing: '#FFD700',
  loved: '#FFB6C1',
  on_fire: '#FF6B6B',
  hopeful: '#F0E68C',
  dreamy: '#DDA0DD',
  free: '#87CEEB',
  peaceful: '#98D8C8',
  energized: '#FF7901',
  calm: '#6495ED',
  thoughtful: '#9370DB',
  focused: '#4A0A66',
  tired: '#A9A9A9',
  melancholy: '#708090',
  strong: '#DC143C',
  angry: '#FF4500',
  sad: '#4682B4',
  grateful: '#98D8C8',
  creative: '#FF7901',
};

const MOOD_EMOJIS = {
  uncloned: '🐑', // Fallback emoji for insights display
  custom_branded: '✨', // Fallback for custom branded mood
  magical: '✨',
  amazing: '🌟',
  loved: '💖',
  on_fire: '🔥',
  hopeful: '🌈',
  dreamy: '💫',
  free: '🦋',
  peaceful: '🌸',
  energized: '⚡',
  calm: '🌙',
  thoughtful: '💭',
  focused: '🎯',
  tired: '😴',
  melancholy: '🌧️',
  strong: '💪',
  angry: '😤',
  sad: '😢',
  grateful: '🙏',
  creative: '🎨',
};

const MOOD_LABELS = {
  uncloned: 'Custom', custom_branded: 'Custom', on_fire: 'On Fire', magical: 'Magical', amazing: 'Amazing',
  loved: 'Loved', hopeful: 'Hopeful', dreamy: 'Dreamy', free: 'Free',
  peaceful: 'Peaceful', energized: 'Energized', calm: 'Calm', thoughtful: 'Thoughtful',
  focused: 'Focused', tired: 'Tired', melancholy: 'Melancholy', strong: 'Strong',
  angry: 'Angry', sad: 'Sad', grateful: 'Grateful', creative: 'Creative', happy: 'Happy'
};

// Collapsible insights section for dashboard
export const InsightsSection = ({ collapsed = true }) => {
  const [isOpen, setIsOpen] = useState(!collapsed);
  const [insights, setInsights] = useState(null);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(false);
  
  // Get custom mood from B2B client config
  const { customMood } = useFeatures();
  
  // Helper to render mood display (emoji or custom image)
  const renderMoodDisplay = (mood, size = 'text-2xl') => {
    // If this is the "uncloned" or "custom_branded" mood and we have a custom mood configured
    if ((mood === 'uncloned' || mood === 'custom_branded') && customMood?.enabled) {
      if (customMood.imageUrl) {
        return (
          <img 
            src={customMood.imageUrl} 
            alt={customMood.label || 'Custom Mood'} 
            className={size === 'text-2xl' ? 'w-8 h-8 object-contain' : 'w-5 h-5 object-contain inline-block'}
          />
        );
      }
      // If no image but has custom label, show first letter or default emoji
      return <span className={size}>{MOOD_EMOJIS[mood] || '✨'}</span>;
    }
    return <span className={size}>{MOOD_EMOJIS[mood] || '✨'}</span>;
  };

  useEffect(() => {
    if (isOpen) {
      fetchInsights();
    }
  }, [isOpen, period]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/insights/moods?period=${period}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      }
    } catch (error) {
      // Silently fail - mood insights are non-critical
      console.debug('Mood insights fetch failed:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const maxEntries = useMemo(() => 
    insights?.chart_data ? Math.max(...insights.chart_data.map(d => d.entries), 1) : 1
  , [insights?.chart_data]);

  // Memoize sorted mood distribution for InsightsSection
  const sortedMoodDistribution = useMemo(() => {
    if (!insights?.mood_distribution) return [];
    return Object.entries(insights.mood_distribution).sort((a, b) => b[1] - a[1]);
  }, [insights?.mood_distribution]);

  const topMoods = useMemo(() => sortedMoodDistribution.slice(0, 5), [sortedMoodDistribution]);

  return (
    <div 
      className="glass-card overflow-hidden mb-6"
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:brightness-110 transition-all"
        data-testid="insights-toggle"
      >
        <div className="flex items-center gap-3">
          <div 
            className="p-2 rounded-lg"
            style={{ background: 'var(--theme-button-bg)' }}
          >
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold" style={{ color: 'var(--theme-text)' }}>Mood Insights</h3>
            <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
              {insights ? `${insights.days_journaled} days journaled this ${period}` : 'View your mood patterns'}
            </p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[var(--theme-primary)]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[var(--theme-primary)]" />
        )}
      </button>

      {/* Collapsible content */}
      {isOpen && (
        <div 
          className="p-4 pt-0"
          style={{ borderTop: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.1)' }}
        >
          {/* Period toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setPeriod('week')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                period === 'week'
                  ? 'text-white'
                  : 'hover:opacity-80'
              }`}
              style={{
                background: period === 'week' ? 'var(--theme-button-bg)' : 'var(--theme-background-secondary)',
                color: period === 'week' ? 'white' : 'var(--theme-text-secondary)'
              }}
            >
              This Week
            </button>
            <button
              onClick={() => setPeriod('month')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                period === 'month'
                  ? 'text-white'
                  : 'hover:opacity-80'
              }`}
              style={{
                background: period === 'month' ? 'var(--theme-button-bg)' : 'var(--theme-background-secondary)',
                color: period === 'month' ? 'white' : 'var(--theme-text-secondary)'
              }}
            >
              This Month
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div 
                className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'transparent' }}
              />
            </div>
          ) : insights ? (
            <div className="space-y-4">
              {/* Mini bar chart */}
              <div className="flex items-end gap-1 h-24">
                {insights.chart_data.map((day) => (
                  <div key={day.date} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full rounded-t-sm transition-all duration-300"
                      style={{
                        height: `${(day.entries / maxEntries) * 100}%`,
                        minHeight: day.entries > 0 ? '8px' : '2px',
                        background: day.entries > 0
                          ? 'var(--theme-button-bg)'
                          : 'var(--theme-background-secondary)',
                      }}
                      title={`${day.date}: ${day.entries} entries`}
                    />
                    <span className="text-[10px] mt-1" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>{day.day}</span>
                  </div>
                ))}
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                <div 
                  className="rounded-xl p-3 text-center"
                  style={{ backgroundColor: 'var(--theme-background-secondary)', opacity: 0.5 }}
                >
                  <p className="text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>{insights.total_entries}</p>
                  <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Entries</p>
                </div>
                <div 
                  className="rounded-xl p-3 text-center"
                  style={{ backgroundColor: 'var(--theme-background-secondary)', opacity: 0.5 }}
                >
                  <p className="text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>{insights.days_journaled}</p>
                  <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Days Active</p>
                </div>
                <div 
                  className="rounded-xl p-3 text-center"
                  style={{ backgroundColor: 'var(--theme-background-secondary)', opacity: 0.5 }}
                >
                  <div className="text-2xl flex items-center justify-center">
                    {insights.dominant_mood ? renderMoodDisplay(insights.dominant_mood, 'text-2xl') : '—'}
                  </div>
                  <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Top Mood</p>
                </div>
              </div>

              {/* Mood distribution */}
              {topMoods.length > 0 && (
                <div>
                  <p className="text-xs mb-2" style={{ color: 'var(--theme-text-secondary)' }}>Mood Distribution</p>
                  <div className="flex flex-wrap gap-2">
                    {topMoods.map(([mood, count]) => (
                        <span
                          key={mood}
                          className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                          style={{
                            backgroundColor: `${MOOD_COLORS[mood] || 'var(--theme-primary)'}20`,
                            color: MOOD_COLORS[mood] || 'var(--theme-primary)',
                            border: `1px solid ${MOOD_COLORS[mood] || 'var(--theme-primary)'}40`,
                          }}
                        >
                          {renderMoodDisplay(mood, 'text-sm')} {count}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center py-4" style={{ color: 'var(--theme-text-secondary)' }}>Start journaling to see your insights!</p>
          )}
        </div>
      )}
    </div>
  );
};

// Full insights page component
export const InsightsPage = () => {
  const [insights, setInsights] = useState(null);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  
  // Get custom mood from B2B client config
  const { customMood } = useFeatures();
  
  // Helper to render mood display (emoji or custom image)
  const renderMoodDisplay = (mood, size = 'text-2xl') => {
    if ((mood === 'uncloned' || mood === 'custom_branded') && customMood?.enabled) {
      if (customMood.imageUrl) {
        return (
          <img 
            src={customMood.imageUrl} 
            alt={customMood.label || 'Custom Mood'} 
            className={size === 'text-2xl' ? 'w-8 h-8 object-contain' : 'w-5 h-5 object-contain inline-block'}
          />
        );
      }
    }
    return <span className={size}>{MOOD_EMOJIS[mood] || '✨'}</span>;
  };

  useEffect(() => {
    fetchInsights();
  }, [period]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/insights/moods?period=${period}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      }
    } catch (error) {
      // Silently fail - mood insights are non-critical
      console.debug('Full mood insights fetch failed:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const maxEntries = useMemo(() => 
    insights?.chart_data ? Math.max(...insights.chart_data.map(d => d.entries), 1) : 1
  , [insights?.chart_data]);

  // Memoize sorted mood distribution for full page view
  const sortedMoodDistribution = useMemo(() => {
    if (!insights?.mood_distribution) return [];
    return Object.entries(insights.mood_distribution).sort((a, b) => b[1] - a[1]);
  }, [insights?.mood_distribution]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
          <TrendingUp className="w-6 h-6 text-[var(--theme-primary)]" />
          Mood Insights
        </h2>
        
        <div className="flex gap-2">
          <button
            onClick={() => setPeriod('week')}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: period === 'week' ? 'var(--theme-button-bg)' : 'var(--theme-background-secondary)',
              color: period === 'week' ? 'white' : 'var(--theme-text-secondary)'
            }}
          >
            Weekly
          </button>
          <button
            onClick={() => setPeriod('month')}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: period === 'month' ? 'var(--theme-button-bg)' : 'var(--theme-background-secondary)',
              color: period === 'month' ? 'white' : 'var(--theme-text-secondary)'
            }}
          >
            Monthly
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div 
            className="w-12 h-12 border-3 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'transparent' }}
          />
        </div>
      ) : insights ? (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Chart card */}
          <div className="glass-card p-6 md:col-span-2">
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
              <Calendar className="w-5 h-5 text-[var(--theme-secondary)]" />
              Journal Activity
            </h3>
            <div className="flex items-end gap-2 h-40">
              {insights.chart_data.map((day) => (
                <div key={day.date} className="flex-1 flex flex-col items-center group">
                  <div className="relative w-full">
                    <div
                      className="w-full rounded-t transition-all duration-300 group-hover:opacity-80"
                      style={{
                        height: `${(day.entries / maxEntries) * 140}px`,
                        minHeight: day.entries > 0 ? '12px' : '4px',
                        background: day.entries > 0
                          ? 'var(--theme-button-bg)'
                          : 'var(--theme-background-secondary)',
                      }}
                    />
                    {day.entries > 0 && (
                      <div 
                        className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 rounded text-xs whitespace-nowrap"
                        style={{ backgroundColor: 'var(--theme-background)', color: 'var(--theme-text)' }}
                      >
                        {day.entries} {day.entries === 1 ? 'entry' : 'entries'}
                      </div>
                    )}
                  </div>
                  <span className="text-xs mt-2" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>{day.day}</span>
                  <span className="text-[10px]" style={{ color: 'var(--theme-text-secondary)', opacity: 0.4 }}>{day.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats cards */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4" style={{ color: 'var(--theme-text)' }}>Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: 'var(--theme-background-secondary)' }}>
                <p className="text-3xl font-bold" style={{ color: 'var(--theme-text)' }}>{insights.total_entries}</p>
                <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>Total Entries</p>
              </div>
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: 'var(--theme-background-secondary)' }}>
                <p className="text-3xl font-bold" style={{ color: 'var(--theme-text)' }}>{insights.days_journaled}</p>
                <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>Days Active</p>
              </div>
              <div className="rounded-xl p-4 text-center col-span-2" style={{ backgroundColor: 'var(--theme-background-secondary)' }}>
                <div className="text-4xl mb-1 flex items-center justify-center">
                  {insights.dominant_mood ? renderMoodDisplay(insights.dominant_mood, 'text-4xl') : '—'}
                </div>
                <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                  Most Common: <span className="capitalize" style={{ color: 'var(--theme-text)' }}>
                    {((insights.dominant_mood === 'uncloned' || insights.dominant_mood === 'custom_branded') && customMood?.enabled && customMood?.label) 
                      ? customMood.label 
                      : (MOOD_LABELS[insights.dominant_mood] || insights.dominant_mood || 'N/A')}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Mood breakdown */}
          <div 
            className="backdrop-blur-md rounded-2xl p-6"
            style={{ 
              backgroundColor: 'rgba(var(--theme-background-rgb), 0.6)',
              border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)'
            }}
          >
            <h3 className="font-semibold mb-4" style={{ color: 'var(--theme-text)' }}>Mood Breakdown</h3>
            {sortedMoodDistribution.length > 0 ? (
              <div className="space-y-3">
                {sortedMoodDistribution.map(([mood, count]) => {
                    const percentage = (count / insights.total_entries) * 100;
                    // Get mood label - use custom mood label for "uncloned" or "custom_branded" if available
                    const moodLabel = ((mood === 'uncloned' || mood === 'custom_branded') && customMood?.enabled && customMood?.label) 
                      ? customMood.label 
                      : (MOOD_LABELS[mood] || mood);
                    return (
                      <div key={mood}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
                            {renderMoodDisplay(mood, 'text-sm')} <span>{moodLabel}</span>
                          </span>
                          <span className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>{count} ({percentage.toFixed(0)}%)</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-background-secondary)', opacity: 0.5 }}>
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${percentage}%`,
                              backgroundColor: MOOD_COLORS[mood],
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="text-center py-4" style={{ color: 'var(--theme-text-secondary)' }}>No mood data yet</p>
            )}
          </div>
        </div>
      ) : (
        <div 
          className="backdrop-blur-md rounded-2xl p-8 text-center"
          style={{ 
            backgroundColor: 'rgba(var(--theme-background-rgb), 0.6)',
            border: '1px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.2)'
          }}
        >
          <p style={{ color: 'var(--theme-text-secondary)' }}>Start journaling to see your insights!</p>
        </div>
      )}
    </div>
  );
};

export default InsightsSection;
